type ScriptOptions = {
  id: string;
  localUrl?: string;
  fallbackUrls: string[];
  ready: () => boolean;
};

type MathJaxRuntime = Record<string, unknown> & {
  startup?: { promise?: Promise<unknown> };
  typesetPromise?: (nodes?: Element[]) => Promise<void>;
};

const scriptLoads = new Map<string, Promise<void>>();
let typesetQueue: Promise<void> = Promise.resolve();

export async function loadScriptOnce(options: ScriptOptions): Promise<void> {
  if (options.ready()) return;
  const pending = scriptLoads.get(options.id);
  if (pending) return pending;
  const existing = document.getElementById(options.id) as HTMLScriptElement | null;
  if (existing) {
    if (existing.dataset.loaded === 'true') {
      if (options.ready()) return;
      existing.remove();
    } else {
      const waiting = new Promise<void>((resolve, reject) => {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`无法加载 ${options.id}`)), { once: true });
      });
      scriptLoads.set(options.id, waiting);
      return waiting.finally(() => scriptLoads.delete(options.id));
    }
  }

  const load = (src: string) => new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => {
      script.remove();
      reject(new Error(`加载 ${src} 超时`));
    }, 3000);
    script.id = options.id;
    script.src = src;
    script.defer = true;
    script.addEventListener('load', () => {
      window.clearTimeout(timeout);
      script.dataset.loaded = 'true';
      resolve();
    }, { once: true });
    script.addEventListener('error', () => {
      window.clearTimeout(timeout);
      script.remove();
      reject(new Error(`无法加载 ${src}`));
    }, { once: true });
    document.head.append(script);
  });

  const task = (async () => {
    if (options.localUrl) {
      try {
        await load(options.localUrl);
        return;
      } catch {
        // Continue with the pinned network fallback.
      }
    }
    let lastError: unknown;
    for (const fallbackUrl of options.fallbackUrls) {
      try {
        await load(fallbackUrl);
        return;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError instanceof Error ? lastError : new Error(`无法加载 ${options.id}`);
  })();
  scriptLoads.set(options.id, task);
  return task.finally(() => scriptLoads.delete(options.id));
}

export async function loadMathJax(chemistry = false): Promise<void> {
  const globalWindow = window as Window & { MathJax?: MathJaxRuntime };
  if (!globalWindow.MathJax) {
    globalWindow.MathJax = {
      loader: {
        paths: { 'mathjax-newcm': '/vendor/mathjax-newcm' },
        ...(chemistry ? { load: ['[tex]/mhchem'] } : {}),
      },
      tex: {
        ...(chemistry ? { packages: { '[+]': ['mhchem'] } } : {}),
        inlineMath: [['\\(', '\\)']],
        displayMath: [['\\[', '\\]']],
      },
      svg: { fontCache: 'global' },
    };
  }
  await loadScriptOnce({
    id: 'mathjax-runtime',
    localUrl: '/vendor/mathjax/tex-svg.js',
    fallbackUrls: [
      'https://cdn.jsdelivr.net/npm/mathjax@4.1.3/tex-svg.js',
      'https://unpkg.com/mathjax@4.1.3/tex-svg.js',
    ],
    ready: () => Boolean(globalWindow.MathJax?.typesetPromise),
  });
  await globalWindow.MathJax?.startup?.promise;
}

export function typeset(root: HTMLElement): Promise<void> {
  const task = typesetQueue.then(async () => {
    const mathJax = (window as Window & { MathJax?: MathJaxRuntime }).MathJax;
    await mathJax?.startup?.promise;
    await mathJax?.typesetPromise?.([root]);
  });
  typesetQueue = task.catch(() => undefined);
  return task;
}
